import {CreateAccessor} from "mobx-graphlink";
import {store} from "../../../../../../Store/index.js";
import {GetTestSegments} from "../../../../../../Server/DB/testSegments.js";
import {GetSelectedSessionExternals_AllowingResim} from "../../../../../../Store/main/timeline/SessionDerivativeData.js";
import {EyeMoveDeclarationType} from "../../../../../../Server/DB/testSegments/@TestSegment.js";
import {GetPromptThresholdColor, sessionChartColors} from "../../SessionChartColors.js";
import {Annotation} from "uplot-vplugins";
import {SessionDataProcessor} from "../../../../../@Shared/Processors/SessionDataProcessor.js";
import {DetectBreathingPeaks} from "../../../../../../Engine/LiveSession/Components/Breath/PeakDetection/PeakDetection_Prominence.js";
import {GetSessionPeriods_Me} from "../../../../../../Server/DB/@Shared/SessionPeriodUtils.js";
import {SessionEvent} from "../../../../../../Engine/LiveSession/SessionEvent.js";
import {minuteInMS} from "web-vcore";
import {Phase} from "../../../../../../Server/DB/@Shared/Phase.js";

export type DreamingPeriod = {preGap: number, startTime: number, endTime: number, duration: number};
export function GetDreamingPeriodsFromSessionEvents(events: SessionEvent[], allowAddPeriodForUnclosedDreamingPhase: boolean) {
	const result = [] as DreamingPeriod[];
	if (events.length == 0) return result;
	let currentDreamStartTime: number|n;
	for (const event of events) {
		if (event.type == "Alarms.PhaseSet") {
			if (event.phase == "dreaming") {
				currentDreamStartTime = event.date;
			}
			if (event.phase != "dreaming" && currentDreamStartTime != null) {
				result.push({
					preGap: currentDreamStartTime - (result.LastOrX()?.endTime ?? events[0].date),
					startTime: currentDreamStartTime, endTime: event.date, duration: event.date - currentDreamStartTime,
				});
				currentDreamStartTime = null;
			}
		}
	}
	if (allowAddPeriodForUnclosedDreamingPhase && currentDreamStartTime != null) {
		const now = Date.now();
		result.push({
			preGap: currentDreamStartTime - (result.LastOrX()?.endTime ?? events[0].date),
			startTime: currentDreamStartTime, endTime: now, duration: now - currentDreamStartTime,
		});
	}
	return result;
}

export type REMPeriod = {preGap: number, startTime: number, endTime: number, dreamingDuration: number, dreamCount: number};
/** This basically founds dreaming-periods that are within 30mins of each other, merging each cluster together as a "REM period". */
export function GetREMPeriodsFromDreamingPeriods(periods: DreamingPeriod[], maxGapBetweenDreams = 30*minuteInMS) {
	const result = [] as REMPeriod[];
	if (periods.length == 0) return result;

	let currentREM_periods = [periods[0]] as DreamingPeriod[];
	let currentREM_startTime = periods[0].startTime;
	let currentREM_endTime = periods[0].endTime;
	const saveCurrentRemPeriod = ()=>{
		result.push({
			// if there's an earlier rem-period, use gap between that and this rem-period
			// else, we're the first rem-period, so just use the preGap value from the first source dreaming-period
			preGap: result.length > 0 ? currentREM_startTime - result.Last().endTime : currentREM_periods[0].preGap,
			// start/end times are simply the start-time of first dream, and end-time of last dream, in this rem-period
			startTime: currentREM_startTime,
			endTime: currentREM_endTime,
			// only include actual dreaming-time in dreaming-duration (ie. exclude the gaps between dreams / when falling back asleep)
			dreamingDuration: currentREM_periods.map(a=>a.duration).Sum(),
			dreamCount: currentREM_periods.length,
		});
	};

	for (let i = 1; i < periods.length; i++) {
		const period = periods[i];
		//const gap = period.preGap;
		const gap = period.startTime - currentREM_endTime;
		// if gap is small, merging with current REM period
		if (gap <= maxGapBetweenDreams) {
			currentREM_periods.push(period);
			currentREM_endTime = period.endTime;
		}
		// else, split this period off into a new REM period
		else {
			// first save current REM period
			saveCurrentRemPeriod();
			// then start new REM period
			currentREM_periods = [period];
			currentREM_startTime = period.startTime;
			currentREM_endTime = period.endTime;
		}
	}
	// add the last REM period
	saveCurrentRemPeriod();

	return result;
}

export const GetDetailChartAnnotations = /*CreateAccessor*/((processor: SessionDataProcessor)=>{
	const uiState = store.main.timeline.sessions;
	const sessionExt = GetSelectedSessionExternals_AllowingResim();
	if (sessionExt == null) return null;
	const session = processor.loadedSessionData?.session
	const events_valid = session ? session.events.filter(a=>!a.invalidated) : [];
	const events_valid_withEnder = session ? events_valid.concat(new SessionEvent({type: "General.SessionEnd", date: session.endTime})) : [];

	const result = [] as Annotation[];

	// drawn before/under series
	// ==========

	const annotationBase_preSeries = {
		drawStage: "afterAxes",
		/*preDraw: ({ctx})=>{
			ctx.globalAlpha = 1.0;
			ctx.globalCompositeOperation = "source-over";
		},*/
	} as const;

	const testSegments = GetTestSegments(store.main.testing.selectedUser, sessionExt.sessionID);
	for (const segment of testSegments) {
		result.push(...segment.eyeMoveDeclarations.map(declaration=>{
			return {
				...annotationBase_preSeries,
				type: "box",
				xMin: {value: declaration.time / 1000, finalize: a=>Math.floor(a)},
				xSize: {pixel: 1},
				yMin: {pixel: "0%"},
				yMax: {pixel: "50%"},
				fillStyle: declaration.type == EyeMoveDeclarationType.Required ? sessionChartColors.eyeMoveDeclaration_required : sessionChartColors.eyeMoveDeclaration_optional,
			} as const;
		}));

		result.push({
			...annotationBase_preSeries,
			type: "box",
			xMin: {value: segment.startTime / 1000},
			xMax: {value: segment.endTime / 1000},
			yMin: {pixel: "0%"},
			yMax: {pixel: "50%"},
			fillStyle: sessionChartColors.testSegmentRegion,
		});
	}

	/*const enabledPrompts = sessionExt.config.eeg.alarmSequence.alarms.filter(a=>a.enabled);
	result.concat(enabledPrompts.map((prompt, index)=>{
		return {
			type: "box",
			fillStyle: GetPromptThresholdColor(index, enabledPrompts.length),
			xMin: {pixel: "0%"},
			xMax: {pixel: "100%"},
			yMin: {value_axis: "z", value: prompt.startOffset},
			ySize: {pixel: 1},
			drawStage: "afterAxes",
		} as const;
	}));*/

	if (uiState.showCurrentTime) {
		result.push({
			...annotationBase_preSeries,
			/*type: "line",
			lineWidth: 1,
			x: {value: uiState.currentTime / 1000},
			color: sessionChartColors.currentTimeLine,*/

			type: "box",
			xMin: {value: uiState.currentTime / 1000}, //, finalize: a=>Math.floor(a)},
			//xMax: {value: uiState.currentTime / 1000, finalize: a=>Math.floor(a) + 1},
			xSize: {pixel: 1, finalize: a=>1},
			yMin: {pixel: "0%"},
			yMax: {pixel: "100%"},
			fillStyle: sessionChartColors.currentTimeLine,
		} as Annotation);
	}

	if (uiState.breath_calcLongInhales) {
		const longInhales = processor.breathProcessor.longInhales;
		for (const attempt of longInhales) {
			const time = attempt.timeOfSignalCompleted / 1000; // convert to seconds
			result.push({
				...annotationBase_preSeries,
				type: "box",
				xMin: {value: time, finalize: a=>Math.floor(a)},
				xSize: {pixel: 1, finalize: a=>1},
				yMin: {pixel: "0%"},
				yMax: {pixel: "100%"},
				fillStyle: sessionChartColors.breath_longInhales,
			} as Annotation);
		}
	}

	if (uiState.breath_calcPeaks) {
		const samples = processor.breathProcessor.samples_force;
		//const peaks = processor.breathProcessor.GetBreathPeaksInRange(0, Date.now());
		const peaks = processor.breathProcessor.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: 0, rangeEndTime: Date.now()});
		for (const peak of peaks) {
			const time = samples[peak.sampleIndex].time / 1000; // convert to seconds
			result.push({
				...annotationBase_preSeries,
				type: "box",
				xMin: {value: time, finalize: a=>Math.floor(a)},
				xSize: {pixel: 1, finalize: a=>1},
				//yMin: {pixel: "50%"},
				yMin: {value: processor.breathProcessor.samples_siren[peak.sampleIndex].value_forDetailChart},
				//yMax: {pixel: "100%"},
				yMax: {value: samples[peak.sampleIndex].value},
				fillStyle: sessionChartColors.breath_peak,
			} as Annotation);
		}
	}

	if (uiState.breath_showIntervalThresholds) {
		if ((sessionExt.config.breath.dream_startWhenBreathIntervalLowRangeBelow ?? 0) > 0) {
			result.push({
				...annotationBase_preSeries,
				type: "box",
				xMin: {pixel: "0%"},
				xMax: {pixel: "100%"},
				yMin: {value: sessionExt.config.breath.dream_startWhenBreathIntervalLowRangeBelow},
				ySize: {pixel: 1, finalize: a=>1},
				fillStyle: sessionChartColors.breath_intervalThresholdLowRange_base,
			} as Annotation);
		}
		if ((sessionExt.config.breath.dream_breathIntervalThreshold ?? 0) > 0) {
			result.push({
				...annotationBase_preSeries,
				type: "box",
				xMin: {pixel: "0%"},
				xMax: {pixel: "100%"},
				yMin: {value: sessionExt.config.breath.dream_breathIntervalThreshold},
				ySize: {pixel: 1, finalize: a=>1},
				fillStyle: sessionChartColors.breath_intervalThreshold_base,
			} as Annotation);
		}
	}

	if (uiState.showSolvingPeriods && session) {
		let lastSolvingStartTime: number|n;
		for (const event of events_valid) {
			if (event.type == "Alarms.PhaseSet" && event.phase == Phase.solving) {
				lastSolvingStartTime = event.date;
			} else if (event.type == "Alarms.PhaseSet" && event.phase != Phase.solving && lastSolvingStartTime != null) {
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: lastSolvingStartTime / 1000, finalize: a=>Math.floor(a)},
					xMax: {value: event.date / 1000, finalize: a=>Math.floor(a) + 1}, // make sure each period shows up as at least 1 pixel wide
					yMin: {pixel: "35%"},
					yMax: {pixel: "65%"},
					fillStyle: sessionChartColors.solvingPeriod,
				} as Annotation);
				lastSolvingStartTime = null;
			}
		}
	}

	if (uiState.showSnoozePeriods && session) {
		let lastSnoozeStartTime: number|n;
		for (const event of events_valid_withEnder) {
			if (event.type == "Snooze.SnoozeStart") {
				lastSnoozeStartTime = event.date;
			} else if ((event.type == "Snooze.SnoozeEnd" || event.type == "General.SessionEnd") && lastSnoozeStartTime != null) {
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: lastSnoozeStartTime / 1000, finalize: a=>Math.floor(a)},
					xMax: {value: event.date / 1000, finalize: a=>Math.floor(a) + 1}, // make sure each period shows up as at least 1 pixel wide
					yMin: {pixel: "40%"},
					yMax: {pixel: "60%"},
					fillStyle: sessionChartColors.snoozePeriod,
				} as Annotation);
				lastSnoozeStartTime = null;
			}
		}
	}

	if (uiState.showDreamingPeriods && session) {
		const dreamingPeriods = GetDreamingPeriodsFromSessionEvents(events_valid, false);
		for (const period of dreamingPeriods) {
			result.push({
				...annotationBase_preSeries,
				type: "box",
				xMin: {value: period.startTime / 1000, finalize: a=>Math.floor(a)},
				xMax: {value: period.endTime / 1000, finalize: a=>Math.floor(a) + 1}, // make sure each period shows up as at least 1 pixel wide
				yMin: {pixel: "0%"},
				yMax: {pixel: "100%"},
				fillStyle: sessionChartColors.dreamPeriod,
			} as Annotation);
		}
	}

	if (uiState.showWakings && session) {
		const sessionPeriods = GetSessionPeriods_Me();
		const associatedSessionPeriod = sessionPeriods.find(a=>a.sessions.Any(b=>b.id == session.id));
		if (associatedSessionPeriod != null) {
			const wakeSegments = associatedSessionPeriod.journalSegments.filter(a=>a.wakeTime != null && !a.excludeFromIntegration);
			const segmentsBeforeWakeSegments = wakeSegments.map(a=>associatedSessionPeriod.journalSegments[associatedSessionPeriod.journalSegments.indexOf(a) - 1]);
			const wakeTimes = wakeSegments.map(a=>a.wakeTime!);
			for (const [i, wakeTime] of wakeTimes.entries()) {
				const segmentBefore = segmentsBeforeWakeSegments[i];
				let fillStyle: typeof CanvasRenderingContext2D.prototype.fillStyle = sessionChartColors.waking_nonLucid;
				if (segmentBefore?.lucid) {
					fillStyle = sessionChartColors.waking_lucid;
				} else if (segmentBefore?.semiLucid) {
					/*const gradientCanvas = new OffscreenCanvas(2, 100);
					const gradientCtx = gradientCanvas.getContext("2d")!;
					const gradient = gradientCtx.createLinearGradient(0, 0, 0, 100);
					gradient.addColorStop(0, sessionChartColors.waking_nonLucid);
					gradient.addColorStop(0.5, sessionChartColors.waking_lucid);
					gradient.addColorStop(1, sessionChartColors.waking_nonLucid);
					gradientCtx.fillStyle = gradient;
					gradientCtx.fillRect(0, 0, 2, 100);
					fillStyle = gradientCtx.createPattern(gradientCanvas, "repeat")!;*/
					fillStyle = sessionChartColors.waking_semiLucid;
				}

				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: wakeTime / 1000, finalize: a=>Math.floor(a)},
					xSize: {pixel: 2, finalize: a=>2},
					yMin: {pixel: "0%"},
					yMax: {pixel: "100%"},
					fillStyle,
				} as Annotation);
			}
		}
	}

	if (uiState.showVisPromptPeriods && session) {
		for (const event of events_valid) {
			if (event.type == "Visualization.StartPrompting") {
				const endEvent = events_valid.find(a=>a.date > event.date && a.type == "Visualization.StopPrompting");
				if (!endEvent) continue;
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: event.date / 1000, finalize: a=>Math.floor(a)},
					xMax: {value: endEvent.date / 1000, finalize: a=>Math.floor(a) + 1}, // make sure each period shows up as at least 1 pixel wide
					yMin: {pixel: "0%"},
					yMax: {pixel: "10%"},
					fillStyle: sessionChartColors.visPromptPeriod,
				} as Annotation);
			}
		}
	}

	if (uiState.showAlertPeriods && session) {
		for (const event of events_valid) {
			if (event.type == "Generic" && event.alerts_setAlertActive?.active == true) {
				const source = event.alerts_setAlertActive?.source;
				const endEvent = events_valid.find(a=>a.date > event.date && a.type == "Generic"
					&& a.alerts_setAlertActive?.source == source && a.alerts_setAlertActive?.active == false);
				if (!endEvent) continue;
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: event.date / 1000, finalize: a=>Math.floor(a)},
					xMax: {value: endEvent.date / 1000, finalize: a=>Math.floor(a) + 1}, // make sure each period shows up as at least 1 pixel wide
					yMin: {pixel: "0%"},
					yMax: {pixel: "5%"},
					fillStyle: sessionChartColors.alertPeriod,
				} as Annotation);
			}
		}
	}

	if (uiState.showSignals && session) {
		for (const event of events_valid) {
			if (event.type == "Signals.SignalPlay") {
				const signal = sessionExt.config.signals.signals.find(s=>s.name == event.signalName);
				const graphColor = signal?.graphColor ?? [255, 255, 255];
				const vMin = signal?.graphVMin ?? 0;
				const vMax = signal?.graphVMax ?? 1;
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: event.date / 1000, finalize: a=>Math.floor(a)},
					xSize: {pixel: 2, finalize: a=>2},
					yMin: {pixel: `${1..Distance(vMin) * 100}%`},
					yMax: {pixel: `${1..Distance(vMax) * 100}%`},
					fillStyle: `rgba(${graphColor[0]},${graphColor[1]},${graphColor[2]},${graphColor[3] ?? 1})`,
				} as Annotation);
			}
		}
	}

	// do this after wakings (so DEILD attempts show up on top of wakings; they're shorter so both should be visible)
	if (uiState.showDeildAttempts && session) {
		for (const event of events_valid) {
			if (event.type == "Breath.DeildAttemptStart") {
				result.push({
					...annotationBase_preSeries,
					type: "box",
					xMin: {value: event.date / 1000, finalize: a=>Math.floor(a)},
					xSize: {pixel: 2, finalize: a=>2},
					yMin: {pixel: "35%"},
					yMax: {pixel: "65%"},
					fillStyle: sessionChartColors.deildAttemptStart,
				} as Annotation);
			}
		}
	}

	// drawn after/over series
	// ==========

	if (uiState.showBaseline) {
		result.push({
			type: "box",
			xMin: {pixel: "0%"},
			xMax: {pixel: "100%"},
			yMin: {value: 0},
			ySize: {pixel: 1, finalize: a=>1},
			fillStyle: sessionChartColors.baseline,
		} as Annotation);
	}

	for (const timeStr of sessionExt.generalHistory.gyroMotion.motionsByTime.VKeys()) {
		const time = Number(timeStr);
		result.push({
			type: "box",
			shouldRender: ({chart})=>{
				const time_xVal = time / 1000;
				return time_xVal >= chart.scales.x.min! && time_xVal <= chart.scales.x.max!;
			},
			xMin: {value: time / 1000, finalize: a=>Math.floor(a)},
			xSize: {pixel: 1},
			yMin: {pixel: "50%"},
			ySize: {pixel: "100%"},
			fillStyle: sessionChartColors.gyroMotionColor,
		});
	}

	return result;
});